import { createContext, useContext, useEffect, useMemo } from "react";

import { setReaderLocale } from "../system/format";
import { browserLocale, isLocale, translate, translateRefusal, type Args, type Locale, type MessageKey, type Refusal } from "./translate";

// ---------------------------------------------------------------------------
// SWEEP §4. The locale the shell resolved, handed to every screen under it.
//
// THREE THINGS FOLLOW ONE VALUE: the words (`useT`), the formatters in
// system/format.ts, and `<html lang>` — which a screen reader uses to choose a
// voice, so Hungarian read in an English voice is the failure it prevents.
//
// No provider means English: the demo gallery renders screens without a shell.
// ---------------------------------------------------------------------------

const LocaleContext = createContext<Locale>("en");

export function LocaleProvider({ locale, children }: { locale: Locale; children: React.ReactNode }) {
  // DURING RENDER as well as in the effect: the screens below format dates in
  // this same render pass, before any effect has run.
  setReaderLocale(locale);
  useEffect(() => {
    setReaderLocale(locale);
    document.documentElement.lang = locale;
    return () => {
      setReaderLocale("en");
      document.documentElement.lang = "en";
    };
  }, [locale]);
  return <LocaleContext.Provider value={locale}>{children}</LocaleContext.Provider>;
}

/**
 * S63. A screen before the session is known: the person's stored choice when the
 * server has already said it, else the browser (translate.ts, browserLocale).
 */
export function preSessionLocale(chosen?: string | null): Locale {
  if (isLocale(chosen)) return chosen;
  return browserLocale(typeof navigator === "undefined" ? undefined : navigator.languages);
}

export function PreSessionLocale({ children }: { children: React.ReactNode }) {
  return <LocaleProvider locale={preSessionLocale()}>{children}</LocaleProvider>;
}

export function useT() {
  const locale = useContext(LocaleContext);
  return useMemo(() => {
    function t<K extends MessageKey>(key: K, ...args: Args<K>): string {
      return translate(locale, key, ...args);
    }
    /** S73. An API refusal in the reader's language; undefined when there is none. */
    function refusal(error: Refusal | null | undefined): string | undefined {
      return translateRefusal(locale, error);
    }
    return { locale, t, refusal };
  }, [locale]);
}
